import { FunctionComponent, useEffect, useState } from "react";
import BasicModal, {
  BasicModalProps,
} from "@components/core/modals/BasicModal";
import {
  MailRecipientDTO,
  RecipientType,
} from "@/state/alertsPreferences/dto/response/mail.recipient.dto";
import { useTranslation } from "react-i18next";
import { Col, Form, Row, Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { useForm } from "antd/es/form/Form";
import MailFormField from "@components/core/inputs/MailFormField";
import { t } from "i18next";

const DEFAULT_RECIPIENT_TYPE: RecipientType = "TO";

const options: DefaultOptionType[] = (["TO", "CC"] as RecipientType[]).map(
  (type) => ({
    label: t(`${"account.myPreferences.recipientType"}.${type}`),
    value: type,
  }),
);

interface AlertPreferencesEmailModalProps extends BasicModalProps {
  mailRecipients: MailRecipientDTO[];
  onSave: (newMailsRecipients: MailRecipientDTO[]) => void;
}

const AlertPreferencesEmailModal: FunctionComponent<
  AlertPreferencesEmailModalProps
> = ({ mailRecipients, onSave, onCancel, ...props }) => {
  const { t } = useTranslation();
  const cloneMailRecipients = (recipients: MailRecipientDTO[]) =>
    recipients.map((r) => ({ ...r }));
  const [updatedMailRecipients, setUpdatedMailRecipients] = useState<
    MailRecipientDTO[]
  >(cloneMailRecipients(mailRecipients));
  const [form] = useForm();

  useEffect(() => {
    setUpdatedMailRecipients(cloneMailRecipients(mailRecipients));
  }, [mailRecipients]);

  const handleSelectMailRecipientType = (
    recipientTypeChosen: RecipientType,
    index: number,
  ) => {
    const newMailRecipients = [...updatedMailRecipients];
    if (newMailRecipients[index]) {
      newMailRecipients[index].recipientType = recipientTypeChosen;
    } else {
      newMailRecipients.push({
        recipientType: recipientTypeChosen,
        email: "",
      });
    }
    setUpdatedMailRecipients(newMailRecipients);
  };

  const handleChangeEmail = (email: string, index: number) => {
    const newMailRecipients = [...updatedMailRecipients];
    if (newMailRecipients[index]) {
      newMailRecipients[index].email = email;
    } else {
      newMailRecipients.push({
        recipientType: DEFAULT_RECIPIENT_TYPE,
        email,
      });
    }
    setUpdatedMailRecipients(newMailRecipients);
  };

  const cleanEmptyMail = (
    mailRecipients: MailRecipientDTO[],
  ): MailRecipientDTO[] => {
    return mailRecipients.filter((recipient) => Boolean(recipient.email));
  };

  const handleSubmit = () => {
    onSave(cleanEmptyMail(updatedMailRecipients));
    onCancel();
  };

  const handleCancel = () => {
    setUpdatedMailRecipients(cloneMailRecipients(mailRecipients));
    onCancel();
  };

  const mailRecipientCell = (index: number, required: boolean = false) => {
    const email = updatedMailRecipients[index]
      ? (updatedMailRecipients[index].email ?? "")
      : "";
    const recipientType = updatedMailRecipients[index]
      ? updatedMailRecipients[index].recipientType
      : "TO";
    return (
      <Row
        key={`AlertPreferencesEmailModal_Row_${index}`}
        className={"mt-2 mb-8"}
      >
        <Col xs={24} sm={4} className={"mt-4 mb-4"}>
          <Select
            options={options}
            onChange={(recipientTypeChosen: RecipientType) => {
              handleSelectMailRecipientType(recipientTypeChosen, index);
            }}
            value={recipientType}
            disabled={required}
          />
        </Col>
        <Col xs={24} sm={20} className={"mt-4 mb-4"}>
          <MailFormField
            onChange={(e) => handleChangeEmail(e.target.value, index)}
            type="text"
            module="modal.alertPreferencesEmail"
            field={`${index}.input`}
            defaultValue={email}
            initialValue={email}
            required={required}
          />
        </Col>
      </Row>
    );
  };

  return (
    <BasicModal
      {...props}
      title={t("modal.alertPreferencesEmail.title")}
      buttonText={t("modal.save")}
      onCancel={handleCancel}
      onOk={form.submit}
    >
      <Form
        className="form-with-error"
        form={form}
        validateTrigger={"onSubmit"}
        onFinish={handleSubmit}
        onFinishFailed={() => {
          console.log("onfinishFailed");
        }}
      >
        {mailRecipientCell(0, true)}
        {mailRecipientCell(1)}
        {mailRecipientCell(2)}
      </Form>
    </BasicModal>
  );
};

export default AlertPreferencesEmailModal;
